"use client";

import * as React from "react";
import { cn } from "@/lib/utils";

export interface PaginationV2Props {
  page?: number;
  currentPage?: number;
  pageSize?: number;
  total?: number;
  totalItems?: number;
  totalPages?: number;
  onPageChange: (_page: number) => void;
  onPageSizeChange?: (_size: number) => void;
  pageSizeOptions?: number[];
  className?: string;
}

export function PaginationV2({
  page,
  currentPage,
  pageSize,
  total,
  totalItems,
  totalPages,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 20, 50],
  className,
}: PaginationV2Props) {
  const resolvedPage = page ?? currentPage ?? 1;
  const resolvedTotal = total ?? totalItems ?? 0;
  const derivedPageSize =
    pageSize ??
    (totalPages && totalPages > 0
      ? Math.max(1, Math.ceil(Math.max(resolvedTotal, 1) / totalPages))
      : 10);
  const resolvedTotalPages =
    totalPages ?? Math.max(1, Math.ceil(resolvedTotal / derivedPageSize));
  const safePage = Math.max(1, Math.min(resolvedPage, resolvedTotalPages));
  const isFirst = safePage <= 1;
  const isLast = safePage >= resolvedTotalPages;

  const rangeStart = Math.min(
    (safePage - 1) * derivedPageSize + 1,
    resolvedTotal
  );
  const rangeEnd = Math.min(safePage * derivedPageSize, resolvedTotal);

  const go = (p: number) => {
    const clamped = Math.max(1, Math.min(p, resolvedTotalPages));
    if (clamped !== resolvedPage) onPageChange(clamped);
  };

  // Build visible page numbers with ellipsis
  const pages = React.useMemo(() => {
    const result: (number | "ellipsis")[] = [];
    const delta = 1;
    const left = Math.max(2, safePage - delta);
    const right = Math.min(resolvedTotalPages - 1, safePage + delta);

    result.push(1);
    if (left > 2) result.push("ellipsis");
    for (let i = left; i <= right; i++) result.push(i);
    if (right < resolvedTotalPages - 1) result.push("ellipsis");
    if (resolvedTotalPages > 1) result.push(resolvedTotalPages);

    return result;
  }, [safePage, resolvedTotalPages]);

  return (
    <nav
      aria-label="Sayfalama"
      className={cn(
        "flex flex-wrap items-center justify-between gap-3 text-sm",
        className
      )}
    >
      {/* Info */}
      <span className="text-[var(--v2-text-muted)]">
        {resolvedTotal === 0
          ? "Kayıt yok"
          : `${rangeStart}–${rangeEnd} / ${resolvedTotal} kayıt`}
      </span>

      <div className="flex items-center gap-1">
        {/* First */}
        <PageBtn label="İlk sayfa" disabled={isFirst} onClick={() => go(1)}>
          ⟨⟨
        </PageBtn>

        {/* Prev */}
        <PageBtn
          label="Önceki sayfa"
          disabled={isFirst}
          onClick={() => go(safePage - 1)}
        >
          ⟨
        </PageBtn>

        {/* Page numbers */}
        {pages.map((p, i) =>
          p === "ellipsis" ? (
            <span
              key={`e-${String(i)}`}
              className="px-1 text-[var(--v2-text-muted)]"
              aria-hidden="true"
            >
              …
            </span>
          ) : (
            <PageBtn
              key={p}
              active={p === safePage}
              label={`Sayfa ${p}`}
              onClick={() => go(p)}
              ariaCurrent={p === safePage ? "page" : undefined}
            >
              {p}
            </PageBtn>
          )
        )}

        {/* Next */}
        <PageBtn
          label="Sonraki sayfa"
          disabled={isLast}
          onClick={() => go(safePage + 1)}
        >
          ⟩
        </PageBtn>

        {/* Last */}
        <PageBtn
          label="Son sayfa"
          disabled={isLast}
          onClick={() => go(resolvedTotalPages)}
        >
          ⟩⟩
        </PageBtn>
      </div>

      {/* Page size selector */}
      {onPageSizeChange && (
        <select
          aria-label="Sayfa başına kayıt"
          className="v2-focus-ring rounded-[var(--v2-radius-md)] border border-[var(--v2-border)] bg-white px-2 py-1.5 text-xs text-[var(--v2-text-strong)]"
          value={derivedPageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
        >
          {pageSizeOptions.map((s) => (
            <option key={s} value={s}>
              {s} / sayfa
            </option>
          ))}
        </select>
      )}
    </nav>
  );
}

/* ---- internal button ---- */

function PageBtn({
  children,
  label,
  disabled,
  active,
  onClick,
  ariaCurrent,
}: {
  children: React.ReactNode;
  label: string;
  disabled?: boolean;
  active?: boolean;
  onClick: () => void;
  ariaCurrent?: "page";
}) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-current={ariaCurrent}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "v2-focus-ring inline-flex h-8 min-w-[32px] items-center justify-center rounded-[var(--v2-radius-md)] text-xs font-semibold transition",
        active
          ? "bg-[var(--v2-accent)] text-white"
          : "text-[var(--v2-text-strong)] hover:bg-slate-100",
        disabled && "cursor-not-allowed opacity-40"
      )}
    >
      {children}
    </button>
  );
}
